import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FileUploadOutlined } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { CsvTemplateHint } from './ui/CsvTemplateHint';
import { templateColumns } from '../utils/csvTemplate';
import { BULK_PRICE_OVERRIDES_TEMPLATE } from '../constants/csvTemplates';

interface BulkOverrideRowResult {
  row: number;
  status: 'saved' | 'would_save' | 'error';
  identifier: string;
  productId?: string;
  price?: number;
  message?: string;
  warnings?: string[];
}

interface BulkOverrideResult {
  total: number;
  saved: number;
  failed: number;
  dryRun: boolean;
  rows: BulkOverrideRowResult[];
}

// Columns the server reads: `BULK_OVERRIDE_HEADER_ALIASES` in `src/validators/storeProductOverrideBulk.ts`
// (applied by `parseBulkOverrideCsv`). A row needs one of sku / upc / productId (`bulkProductOverrideRowSchema`)
// and a `price`; `brand` only disambiguates a SKU shared by several brands. The first two rows are examples.
const BULK_REQUIRED: Record<string, boolean | 'one-of'> = {
  sku: 'one-of',
  upc: 'one-of',
  productId: 'one-of',
  price: true,
};

const money = (value?: number) =>
  typeof value === 'number' ? value.toLocaleString(undefined, { style: 'currency', currency: 'USD' }) : '';

/**
 * "Upload CSV" next to "Add override" (flag `bulk_price_overrides`): a template,
 * a file picker, a dry-run check, and the server's per-row result. Renders
 * nothing while the flag is off for the organization.
 */
const BulkPriceOverrideAction: React.FC = () => {
  const { enabled } = useOrgFeatureFlag('bulk_price_overrides');
  const { t } = useAppTranslation();
  const { enabled: keepRebateEnabled } = useOrgFeatureFlag('override_keeps_rebate');
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [fileName, setFileName] = useState('');
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState<BulkOverrideResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: async (dryRun: boolean): Promise<BulkOverrideResult> => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/store/product-overrides/bulk', {
        method: 'POST',
        headers,
        body: JSON.stringify({ csv, dryRun }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body?.error?.message || body?.message || t('pricing.bulkOverrides.requestFailed'));
      }
      return body.data as BulkOverrideResult;
    },
    onSuccess: (data) => {
      setError(null);
      setResult(data);
      if (!data.dryRun && data.saved > 0) {
        queryClient.invalidateQueries({ queryKey: ['price-overrides'] });
        queryClient.invalidateQueries({ queryKey: ['product-overrides'] });
      }
    },
    onError: (err: unknown) => {
      setResult(null);
      setError(err instanceof Error ? err.message : t('pricing.bulkOverrides.requestFailed'));
    },
  });

  if (!enabled) return null;

  const reset = () => {
    setFileName('');
    setCsv('');
    setResult(null);
    setError(null);
    send.reset();
  };

  const close = () => {
    setOpen(false);
    reset();
  };

  const pickFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      setFileName(file.name);
      setCsv(text);
      setResult(null);
      setError(null);
    } catch {
      setError(t('pricing.bulkOverrides.readFailed'));
    }
  };

  // The keepRebate column is offered only with its flag on (MOT-874).
  const shownColumns = BULK_PRICE_OVERRIDES_TEMPLATE.headers.map((name, index) => ({ name, index })).filter(({ name }) => keepRebateEnabled || name !== 'keepRebate');
  const templateHeaders = shownColumns.map(({ name }) => name);
  const templateRows = BULK_PRICE_OVERRIDES_TEMPLATE.rows.map((row) => shownColumns.map(({ index }) => row[index]));
  const templateProps = {
    fileName: BULK_PRICE_OVERRIDES_TEMPLATE.fileName,
    columns: templateColumns(
      templateHeaders,
      templateRows,
      Object.fromEntries(
        templateHeaders.map((name) => [
          name,
          { required: BULK_REQUIRED[name], description: t(`pricing.bulkOverrides.columns.${name}`) },
        ]),
      ),
    ),
    exampleRows: templateRows,
  };

  const statusChip = (status: BulkOverrideRowResult['status']) => (
    <Chip
      size="small"
      variant="outlined"
      color={status === 'error' ? 'warning' : 'success'}
      label={
        status === 'saved'
          ? t('pricing.bulkOverrides.statusSaved')
          : status === 'would_save'
            ? t('pricing.bulkOverrides.statusReady')
            : t('pricing.bulkOverrides.statusError')
      }
      sx={{ height: 22, fontSize: '0.7rem' }}
    />
  );

  const summary = result
    ? result.dryRun
      ? t('pricing.bulkOverrides.summaryChecked', { saved: result.saved, total: result.total, failed: result.failed })
      : result.failed > 0
        ? `${t('pricing.bulkOverrides.summarySaved', { saved: result.saved, total: result.total })} ${t('pricing.bulkOverrides.summaryFailed', { failed: result.failed })}`
        : t('pricing.bulkOverrides.summarySaved', { saved: result.saved, total: result.total })
    : null;

  return (
    <>
      <Button
        size="small"
        startIcon={<FileUploadOutlined />}
        onClick={() => setOpen(true)}
        sx={{ fontWeight: 500, fontSize: '0.8rem' }}
      >
        {t('pricing.bulkOverrides.button')}
      </Button>
      <Dialog open={open} onClose={close} maxWidth="md" fullWidth fullScreen={fullScreen}>
        <DialogTitle>{t('pricing.bulkOverrides.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('pricing.bulkOverrides.intro')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            <CsvTemplateHint {...templateProps} compact />
            <Button size="small" variant="outlined" startIcon={<FileUploadOutlined />} onClick={() => inputRef.current?.click()}>
              {t('pricing.bulkOverrides.chooseFile')}
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={pickFile}
              aria-label={t('pricing.bulkOverrides.chooseFile')}
            />
            <Typography variant="caption" color="text.secondary">
              {fileName || t('pricing.bulkOverrides.noFile')}
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <CsvTemplateHint {...templateProps} explainerOnly intro={t('pricing.bulkOverrides.columns.intro')} />
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {summary && (
            <Alert severity={result && result.failed > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
              {summary}
              {result && !result.dryRun && result.saved > 0 ? ` ${t('pricing.bulkOverrides.savedNote')}` : ''}
            </Alert>
          )}

          {result && (
            <TableContainer sx={{ maxHeight: 360, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('pricing.bulkOverrides.colRow')}</TableCell>
                    <TableCell>{t('pricing.bulkOverrides.colProduct')}</TableCell>
                    <TableCell align="right">{t('pricing.bulkOverrides.colPrice')}</TableCell>
                    <TableCell>{t('pricing.bulkOverrides.colResult')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.rows.map((row) => (
                    <TableRow key={row.row}>
                      <TableCell>{row.row}</TableCell>
                      <TableCell sx={{ wordBreak: 'break-word' }}>{row.identifier || '—'}</TableCell>
                      <TableCell align="right">{money(row.price)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                          {statusChip(row.status)}
                          {row.message && <Typography variant="caption" color="text.secondary">{row.message}</Typography>}
                          {row.warnings?.map((warning) => (
                            <Typography key={warning} variant="caption" color="text.secondary">{warning}</Typography>
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>{t('pricing.bulkOverrides.close')}</Button>
          <Button
            variant="outlined"
            disabled={!csv || send.isPending}
            onClick={() => send.mutate(true)}
            startIcon={send.isPending && send.variables === true ? <CircularProgress size={14} /> : undefined}
          >
            {send.isPending && send.variables === true ? t('pricing.bulkOverrides.checking') : t('pricing.bulkOverrides.check')}
          </Button>
          <Button
            variant="contained"
            disabled={!csv || send.isPending}
            onClick={() => send.mutate(false)}
            startIcon={send.isPending && send.variables === false ? <CircularProgress size={14} /> : undefined}
          >
            {send.isPending && send.variables === false ? t('pricing.bulkOverrides.uploading') : t('pricing.bulkOverrides.upload')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkPriceOverrideAction;
